import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { useI18nStore } from "@/stores/i18nStore";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error?: Error | undefined;
  errorInfo?: ErrorInfo | undefined;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          title={this.props.fallbackTitle}
          message={this.props.fallbackMessage}
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

/** The fallback screen, a function component so it can read the app language. */
function ErrorFallback({
  title,
  message,
  error,
  errorInfo,
  onRetry,
}: {
  title?: string;
  message?: string;
  error?: Error;
  errorInfo?: ErrorInfo;
  onRetry: () => void;
}) {
  const { t } = useI18nStore();
  return (
    <div className="p-6 flex items-center justify-center min-h-[400px]">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900">
            {title ?? t.errbTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          <p className="text-gray-600 leading-relaxed">
            {message ?? t.errbMessage}
          </p>

          {process.env.NODE_ENV === "development" && error && (
            <details className="text-left">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 mb-2 flex items-center gap-2">
                <span>▶</span> {t.errbDeveloperDetails}
              </summary>
              <pre className="text-xs bg-gray-50 p-3 rounded border border-gray-200 overflow-auto max-h-40 font-mono text-red-600">
                {error.toString()}
                {errorInfo?.componentStack}
              </pre>
            </details>
          )}

          <div className="flex gap-3 justify-center pt-2">
            <Button onClick={onRetry} variant="outline" className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" />
              {t.errbRetry}
            </Button>

            <Button
              onClick={() => (window.location.href = "/")}
              variant="primary"
              className="flex-1"
            >
              <Home className="mr-2 h-4 w-4" />
              {t.errbHome}
            </Button>
          </div>

          <p className="text-xs text-gray-500 pt-2">{t.errbContactSupport}</p>
        </CardContent>
      </Card>
    </div>
  );
}

export default ErrorBoundary;
